import { createBrowserRouter, Navigate, Outlet } from 'react-router';

import { AppProviders } from '@/app/AppProviders';
import { FullPageSpinner } from '@/components/common/LoadingState';
import { AppShell } from '@/components/layout/AppShell';
import NotFoundPage from '@/pages/NotFoundPage';
import RouteErrorPage from '@/pages/RouteErrorPage';

import { GuestRoute } from './GuestRoute';
import { paths } from './paths';
import { ProtectedRoute } from './ProtectedRoute';

/**
 * Data-mode router (`createBrowserRouter`), chosen over <BrowserRouter><Routes>
 * for two things: `errorElement` per route branch, and `lazy()` code splitting
 * without any <Suspense> choreography — worth it because Fluent's DataGrid is a
 * heavy import the sign-in page should never pay for.
 *
 * DELIBERATELY NO `loader` OR `action`. TanStack Query owns all server state;
 * adding route loaders would create a second, unsynchronised cache and force
 * awkward `queryClient.ensureQueryData` bridges. Please don't add one.
 */
function RootLayout() {
  return (
    <AppProviders>
      <Outlet />
    </AppProviders>
  );
}

function DocumentViewerFallback() {
  return <FullPageSpinner label="Opening the viewer…" />;
}

export const routes = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <GuestRoute />,
        children: [
          {
            path: paths.login,
            lazy: async () => ({ Component: (await import('@/pages/LoginPage')).default }),
          },
          {
            path: paths.forgotPassword,
            lazy: async () => ({ Component: (await import('@/pages/ForgotPasswordPage')).default }),
          },
          {
            path: paths.resetPassword,
            lazy: async () => ({ Component: (await import('@/pages/ResetPasswordPage')).default }),
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          // Outside AppShell on purpose: a page the user cannot leave should
          // not be framed by navigation that all bounces back to it.
          {
            path: paths.changePassword,
            lazy: async () => ({ Component: (await import('@/pages/ChangePasswordPage')).default }),
          },
          // Also outside AppShell, for the opposite reason: the Office frame
          // wants the whole viewport, and the shell's navigation would halve
          // the space the document gets. The page carries its own way back.
          {
            path: paths.documentViewer,
            lazy: async () => ({
              Component: (await import('@/pages/DocumentViewerPage')).default,
            }),
            // The viewer always opens in a fresh tab, so its code is never
            // already loaded — without this the tab sits empty while it arrives.
            HydrateFallback: DocumentViewerFallback,
          },
          {
            element: <AppShell />,
            children: [
              { index: true, element: <Navigate to={paths.dashboard} replace /> },
              {
                path: paths.dashboard,
                lazy: async () => ({ Component: (await import('@/pages/DashboardPage')).default }),
              },
              {
                path: paths.documents,
                lazy: async () => ({ Component: (await import('@/pages/DocumentsPage')).default }),
              },
              {
                path: paths.profile,
                lazy: async () => ({ Component: (await import('@/pages/ProfilePage')).default }),
              },
              {
                path: paths.settings,
                lazy: async () => ({ Component: (await import('@/pages/SettingsPage')).default }),
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
